import React, { useState } from 'react';
import { SearchIcon, XIcon, FilterIcon, ArrowRightIcon, ArrowLeftIcon, DownloadIcon, ExternalLinkIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
const PolicySearch = () => {
  const [searchTerm, setSearchTerm] = useState('Peripheral Nerve Stimulation');
  const [expandedResult, setExpandedResult] = useState<number | null>(null);
  const [activeFilters, setActiveFilters] = useState(['Peripheral Nerve Stimulation']);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [viewMode, setViewMode] = useState<'relevance' | 'semantic'>('relevance');
  const removeFilter = (filter: string) => {
    setActiveFilters(activeFilters.filter(f => f !== filter));
  };
  const toggleFilterPanel = () => {
    setShowFilterPanel(!showFilterPanel);
  };
  const toggleResultExpand = (id: number) => {
    if (expandedResult === id) {
      setExpandedResult(null);
    } else {
      setExpandedResult(id);
    }
  };
  // Sample search results
  const searchResults = [{
    id: 1,
    title: 'Peripheral Nerve Stimulation of the Trunk or Limbs for Treatment of Pain',
    payer: 'Blue Cross and Blue Shield of Alabama',
    status: 'positive',
    category: 'Neurology',
    date: '10/01/2024',
    relevanceScore: 98,
    snippet: "...may be considered <mark>medically necessary</mark> for the treatment of chronic intractable pain that is refractory to all other pain therapies, when ALL of the following criteria, clearly documented in the individual's record, are met...",
    keyFindings: ['Covers peripheral nerve stimulation for chronic pain', 'Requires documented failure of conservative treatments', 'Specific inclusion of trunk and limb applications', 'Requires specialist evaluation'],
    productMentions: ['Freedom PNS System (covered)', 'Sprint PNS System (covered)'],
    codes: ['64555', '64575', '64590', '64595']
  }, {
    id: 2,
    title: 'Electrical Stimulation for Pain',
    payer: 'Aetna',
    status: 'negative',
    category: 'Durable Medical Equipment',
    date: '04/14/2025',
    relevanceScore: 95,
    snippet: '...The following durable medical equipment (DME) and supplies are considered <mark>experimental, investigational, or unproven</mark> because the effectiveness of these approaches for the specified indication(s) has not been established: Peripheral subcutaneous field stimulation (PSFS) or peripheral nerve field stimulation (PNFS) (e.g., the Freedom PNS System, the Sprint PNS System, the StimQ PNS System)...',
    keyFindings: ['Explicitly lists Freedom PNS System as experimental', 'Classifies all peripheral nerve field stimulation as investigational', 'No coverage pathway available', 'Specific exclusion for hemiplegic shoulder pain'],
    productMentions: ['Freedom PNS System (excluded)', 'Sprint PNS System (excluded)', 'StimQ PNS System (excluded)'],
    codes: ['64555']
  }, {
    id: 3,
    title: 'Peripheral Subcutaneous Field Stimulation And Peripheral Nerve Stimulation',
    payer: 'Blue Cross Blue Shield of Michigan',
    status: 'negative',
    category: 'Medicare Advantage Policy',
    date: '03/15/2025',
    relevanceScore: 92,
    snippet: '...Peripheral subcutaneous field stimulation is <mark>considered experimental and investigational</mark> for all indications including but not limited to the treatment of back pain, neck pain, headache, abdominal pain, pelvic pain, and post-herpetic neuralgia...',
    keyFindings: ['Classifies peripheral subcutaneous field stimulation as experimental', 'Distinguishes between permanent and temporary implantation', 'No coverage for any pain indications', 'References Medicare National Coverage Determination'],
    productMentions: ['No specific product mentions'],
    codes: ['64555', '64575', '64590', '64595', '64999']
  }, {
    id: 4,
    title: 'Implantable Peripheral Nerve Stimulation Devices as a Treatment for Pain',
    payer: 'Blue Cross and Blue Shield of Louisiana',
    status: 'negative',
    category: 'Neurology',
    date: '05/15/2025',
    relevanceScore: 90,
    snippet: '...Based on review of available data, the Company considers implantable peripheral nerve stimulation devices for the treatment of pain to be <mark>investigational</mark>. The scientific evidence is insufficient to demonstrate that implantable peripheral nerve stimulation devices improve health outcomes...',
    keyFindings: ['Classifies all implantable PNS devices as investigational', 'Cites insufficient scientific evidence', 'No distinction between temporary and permanent devices', 'References Hayes Technology Assessment with "D" rating'],
    productMentions: ['No specific product mentions'],
    codes: ['64555', '64575', '64590', '64595']
  }, {
    id: 5,
    title: 'Peripheral Subcutaneous Field Stimulation (PSFS)',
    payer: 'Capital BlueCross',
    status: 'negative',
    category: 'Neurological',
    date: '02/01/2025',
    relevanceScore: 88,
    snippet: '...Peripheral subcutaneous field stimulation is <mark>considered investigational</mark> for all indications, including but not limited to treatment of chronic pain. Scientific evidence in peer-reviewed medical literature does not support the efficacy of this treatment...',
    keyFindings: ['Classifies all PSFS as investigational', 'No coverage for any pain indications', 'Cites lack of peer-reviewed evidence', 'No appeal pathway indicated'],
    productMentions: ['No specific product mentions'],
    codes: ['64555', '64999']
  }, {
    id: 6,
    title: 'Implantable Peripheral Nerve Stimulation for Chronic Pain Conditions',
    payer: 'Blue Cross and Blue Shield of Wyoming',
    status: 'positive',
    category: 'Surgery',
    date: '11/15/2024',
    relevanceScore: 85,
    snippet: '...Implantable peripheral nerve stimulation may be <mark>medically necessary</mark> when ALL of the following criteria are met: 1) The patient has chronic, intractable pain resulting from a peripheral nerve injury; 2) Other treatment modalities (e.g., pharmacologic, physical therapy, etc.) have been tried and failed...',
    keyFindings: ['Covers PNS for specific chronic pain conditions', 'Requires documented failure of conservative treatments', 'Requires specialist evaluation and trial period', 'Specific coverage for peripheral nerve injuries'],
    productMentions: ['No specific product mentions'],
    codes: ['64555', '64575', '64590', '64595']
  }, {
    id: 7,
    title: 'Billing and Coding: Auricular Peripheral Nerve Stimulation (Electro-Acupuncture Device)',
    payer: 'Novitas Solutions (CMS)',
    status: 'negative',
    category: 'Billing and Coding',
    date: '09/01/2024',
    relevanceScore: 78,
    snippet: '...Auricular peripheral nerve field stimulation is <mark>not reasonable and necessary</mark> for the treatment of acute or chronic pain. This procedure is considered investigational...',
    keyFindings: ['Specific to auricular peripheral nerve stimulation', 'Not directly applicable to trunk/limb PNS', 'Medicare contractor determination', 'Coding guidance for non-covered service'],
    productMentions: ['No specific product mentions'],
    codes: ['0451T', '0452T', '0453T', '0454T']
  }, {
    id: 8,
    title: 'Nerve Stimulation Devices for the Treatment of Pain',
    payer: 'Cigna',
    status: 'restricted',
    category: 'Therapy/Rehabilitation',
    date: '06/15/2025',
    relevanceScore: 75,
    snippet: '...Peripheral nerve field stimulation (PNFS) is <mark>considered medically necessary</mark> for an individual with chronic intractable neuropathic pain when ALL of the following criteria are met: (see policy for specific criteria)...',
    keyFindings: ['Covers PNFS with significant restrictions', 'Requires specific neuropathic pain diagnoses', 'Requires minimum 6-month conservative therapy trial', 'Requires psychological evaluation'],
    productMentions: ['No specific product mentions'],
    codes: ['64555', '64575', '64590', '64595']
  }];
  // Filter categories
  const filterCategories = [{
    name: 'Coverage Status',
    options: [{
      id: 'positive',
      label: 'Positive',
      count: 2
    }, {
      id: 'negative',
      label: 'Negative',
      count: 5
    }, {
      id: 'restricted',
      label: 'Restricted',
      count: 1
    }, {
      id: 'no-policy',
      label: 'No Policy',
      count: 0
    }]
  }, {
    name: 'Payer Type',
    options: [{
      id: 'commercial',
      label: 'Commercial',
      count: 6
    }, {
      id: 'medicare',
      label: 'Medicare',
      count: 2
    }, {
      id: 'medicaid',
      label: 'Medicaid',
      count: 0
    }]
  }, {
    name: 'Policy Category',
    options: [{
      id: 'neurology',
      label: 'Neurology',
      count: 3
    }, {
      id: 'surgery',
      label: 'Surgery',
      count: 1
    }, {
      id: 'dme',
      label: 'Durable Medical Equipment',
      count: 1
    }, {
      id: 'therapy',
      label: 'Therapy/Rehabilitation',
      count: 1
    }, {
      id: 'billing',
      label: 'Billing and Coding',
      count: 2
    }]
  }, {
    name: 'Product Mention',
    options: [{
      id: 'freedom',
      label: 'Freedom PNS System',
      count: 2
    }, {
      id: 'sprint',
      label: 'Sprint PNS System',
      count: 2
    }, {
      id: 'stimq',
      label: 'StimQ PNS System',
      count: 1
    }]
  }, {
    name: 'CPT/HCPCS Codes',
    options: [{
      id: '64555',
      label: '64555',
      count: 7
    }, {
      id: '64575',
      label: '64575',
      count: 4
    }, {
      id: '64590',
      label: '64590',
      count: 4
    }, {
      id: '64595',
      label: '64595',
      count: 4
    }, {
      id: '64999',
      label: '64999',
      count: 2
    }]
  }];
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-1593">
      <div className="mb-6" data-id="element-1594">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1595">
          <div data-id="element-1596">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1597">
              Policy Intelligence Search
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1598">
              Semantic search across all payer policies with AI-powered insights
            </p>
          </div>
          <div className="mt-4 md:mt-0" data-id="element-1599">
            <div className="p-1 bg-white rounded-lg shadow flex" data-id="element-1600">
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${viewMode === 'relevance' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setViewMode('relevance')} data-id="element-1601">
                Relevance View
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${viewMode === 'semantic' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setViewMode('semantic')} data-id="element-1602">
                Semantic Analysis
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Search box */}
      <div className="mb-8" data-id="element-1603">
        <div className="relative" data-id="element-1604">
          <div className="flex" data-id="element-1605">
            <div className="relative flex-grow" data-id="element-1606">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1607">
                <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-1608" />
              </div>
              <input type="text" className="block w-full py-3 pl-10 pr-3 text-gray-900 bg-white border border-gray-300 rounded-l-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search across all payer policies..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1609" />
            </div>
            <button className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-r-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-1610">
              Search
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center mt-3 ml-2 gap-2" data-id="element-1611">
          {activeFilters.map(filter => <div key={filter} className="flex items-center px-3 py-1 bg-blue-100 rounded-full" data-id="element-1612">
              <span className="text-sm text-blue-800" data-id="element-1613">{filter}</span>
              <button className="ml-1 text-blue-500 hover:text-blue-700" onClick={() => removeFilter(filter)} data-id="element-1614">
                <XIcon className="w-4 h-4" data-id="element-1615" />
              </button>
            </div>)}
          <span className="text-sm text-gray-500" data-id="element-1616">
            Showing {searchResults.length} of 323 results for "
            {activeFilters.join(', ')}"
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4" data-id="element-1617">
        {/* Filters panel - can be toggled on mobile */}
        <div className={`lg:block ${showFilterPanel ? 'block' : 'hidden'}`} data-id="element-1618">
          <div className="bg-white rounded-lg shadow" data-id="element-1619">
            <div className="p-4 border-b border-gray-200" data-id="element-1620">
              <div className="flex items-center justify-between" data-id="element-1621">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1622">Filters</h3>
                <button className="lg:hidden text-gray-500 hover:text-gray-700" onClick={toggleFilterPanel} data-id="element-1623">
                  <XIcon className="w-5 h-5" data-id="element-1624" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-6" data-id="element-1625">
              {filterCategories.map(category => <div key={category.name} className="space-y-2" data-id="element-1626">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-1627">
                    {category.name}
                  </h4>
                  <div className="space-y-1" data-id="element-1628">
                    {category.options.map(option => <div key={option.id} className="flex items-center" data-id="element-1629">
                        <input id={option.id} name={option.id} type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1630" />
                        <label htmlFor={option.id} className="ml-2 text-sm text-gray-700" data-id="element-1631">
                          {option.label} ({option.count})
                        </label>
                      </div>)}
                  </div>
                </div>)}
              <div className="pt-4 border-t border-gray-200 flex justify-between" data-id="element-1632">
                <button className="text-sm text-gray-600 hover:text-gray-800" data-id="element-1633">
                  Clear All
                </button>
                <button className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1634">
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Search results */}
        <div className="lg:col-span-3" data-id="element-1635">
          {/* Mobile filter toggle */}
          <div className="mb-4 lg:hidden" data-id="element-1636">
            <button className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 flex items-center justify-center" onClick={toggleFilterPanel} data-id="element-1637">
              <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-1638" />
              Filter Results
            </button>
          </div>
          {/* Semantic Analysis View */}
          {viewMode === 'semantic' && <div className="mb-6 bg-white rounded-lg shadow overflow-hidden" data-id="element-1639">
              <div className="p-4 border-b border-gray-200" data-id="element-1640">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1641">
                  Semantic Analysis
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-1642">
                  AI-generated insights from your search results
                </p>
              </div>
              <div className="p-4" data-id="element-1643">
                <div className="mb-6" data-id="element-1644">
                  <h4 className="text-base font-medium text-gray-900 mb-3" data-id="element-1645">
                    Key Findings
                  </h4>
                  <div className="p-4 bg-blue-50 rounded-lg" data-id="element-1646">
                    <ul className="space-y-2 text-sm text-gray-800" data-id="element-1647">
                      <li data-id="element-1648">
                        <strong data-id="element-1649">Coverage Status:</strong> 25% positive coverage
                        (2/8 policies), 62.5% negative (5/8), 12.5% restricted
                        (1/8)
                      </li>
                      <li data-id="element-1650">
                        <strong data-id="element-1651">Regional Patterns:</strong> BCBS entities show
                        mixed coverage with regional variations (Alabama and
                        Wyoming positive, Michigan and Louisiana negative)
                      </li>
                      <li data-id="element-1652">
                        <strong data-id="element-1653">Product Mentions:</strong> Freedom PNS and
                        Sprint PNS explicitly mentioned in 2 policies (1
                        positive, 1 negative)
                      </li>
                      <li data-id="element-1654">
                        <strong data-id="element-1655">Coverage Criteria:</strong> Positive policies
                        require documented failure of conservative treatments,
                        specialist evaluation, and specific pain conditions
                      </li>
                      <li data-id="element-1656">
                        <strong data-id="element-1657">Denial Rationale:</strong> Most negative
                        policies cite "experimental/investigational" status due
                        to insufficient evidence
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mb-6" data-id="element-1658">
                  <h4 className="text-base font-medium text-gray-900 mb-3" data-id="element-1659">
                    Coverage Trends
                  </h4>
                  <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1660">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1661">
                      <div data-id="element-1662">
                        <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1663">
                          Positive Policy Elements
                        </h5>
                        <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1" data-id="element-1664">
                          <li data-id="element-1665">Coverage for chronic intractable pain</li>
                          <li data-id="element-1666">Coverage for peripheral nerve injuries</li>
                          <li data-id="element-1667">
                            Specific inclusion of trunk and limb applications
                          </li>
                          <li data-id="element-1668">Clear criteria for medical necessity</li>
                        </ul>
                      </div>
                      <div data-id="element-1669">
                        <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1670">
                          Negative Policy Elements
                        </h5>
                        <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1" data-id="element-1671">
                          <li data-id="element-1672">
                            Classification as "experimental/investigational"
                          </li>
                          <li data-id="element-1673">Specific exclusion of PSFS/PNFS technologies</li>
                          <li data-id="element-1674">Citation of insufficient scientific evidence</li>
                          <li data-id="element-1675">
                            Product-specific exclusions (Freedom PNS, etc.)
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-1676">
                  <h4 className="text-base font-medium text-gray-900 mb-3" data-id="element-1677">
                    Recommendations
                  </h4>
                  <div className="p-4 bg-green-50 rounded-lg" data-id="element-1678">
                    <ul className="pl-5 list-disc text-sm text-green-800 space-y-1" data-id="element-1679">
                      <li data-id="element-1680">
                        Leverage BCBS Alabama and Wyoming policy language for
                        advocacy with other BCBS entities
                      </li>
                      <li data-id="element-1681">
                        Address "insufficient evidence" claims with targeted
                        clinical data packages
                      </li>
                      <li data-id="element-1682">
                        Focus on distinguishing your technology from broadly
                        excluded PSFS/PNFS categories
                      </li>
                      <li data-id="element-1683">
                        Develop strategy for upcoming Cigna policy review (most
                        favorable of restricted policies)
                      </li>
                      <li data-id="element-1684">
                        Monitor BCBS policy updates closely due to significant
                        regional variations
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>}
          {/* Results list */}
          <div className="space-y-4" data-id="element-1685">
            {searchResults.map(result => <div key={result.id} className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1686">
                <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => toggleResultExpand(result.id)} data-id="element-1687">
                  <div className="flex items-start justify-between" data-id="element-1688">
                    <div data-id="element-1689">
                      <div className="flex items-center" data-id="element-1690">
                        <StatusBadge status={result.status as any} size="sm" data-id="element-1691" />
                        <span className="ml-2 text-xs text-gray-500" data-id="element-1692">
                          {result.date}
                        </span>
                        {viewMode === 'relevance' && <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-full" data-id="element-1693">
                            {result.relevanceScore}% match
                          </span>}
                      </div>
                      <h3 className="mt-1 text-lg font-medium text-gray-900" data-id="element-1694">
                        {result.title}
                      </h3>
                      <p className="text-sm text-blue-600" data-id="element-1695">{result.payer}</p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1696">
                        Category: {result.category}
                      </p>
                    </div>
                    <div data-id="element-1697">
                      {expandedResult === result.id ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-1698" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-1699" />}
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-700 bg-gray-50 p-3 rounded" data-id="element-1700">
                    <div dangerouslySetInnerHTML={{
                  __html: result.snippet
                }} data-id="element-1701" />
                  </div>
                </div>
                {expandedResult === result.id && <div className="px-4 py-3 border-t border-gray-200 bg-gray-50" data-id="element-1702">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1703">
                      <div data-id="element-1704">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1705">
                          Key Findings
                        </h4>
                        <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1" data-id="element-1706">
                          {result.keyFindings.map((finding, idx) => <li key={idx} data-id="element-1707">{finding}</li>)}
                        </ul>
                      </div>
                      <div data-id="element-1708">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1709">
                          Product Mentions
                        </h4>
                        <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1" data-id="element-1710">
                          {result.productMentions.map((mention, idx) => <li key={idx} data-id="element-1711">{mention}</li>)}
                        </ul>
                        <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2" data-id="element-1712">
                          CPT/HCPCS Codes
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-1713">
                          {result.codes.map(code => <span key={code} className="px-2 py-1 text-xs bg-gray-200 text-gray-800 rounded" data-id="element-1714">
                              {code}
                            </span>)}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3" data-id="element-1715">
                      <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 flex items-center" data-id="element-1716">
                        <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1717" />
                        Export
                      </button>
                      <button className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center" data-id="element-1718">
                        <ExternalLinkIcon className="w-4 h-4 mr-2" data-id="element-1719" />
                        View Full Policy
                      </button>
                    </div>
                  </div>}
              </div>)}
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-6" data-id="element-1720">
            <div className="flex items-center" data-id="element-1721">
              <p className="text-sm text-gray-700" data-id="element-1722">
                Showing <span className="font-medium" data-id="element-1723">1</span> to{' '}
                <span className="font-medium" data-id="element-1724">8</span> of{' '}
                <span className="font-medium" data-id="element-1725">323</span> results
              </p>
            </div>
            <div className="flex space-x-2" data-id="element-1726">
              <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50" disabled data-id="element-1727">
                <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1728" />
                Previous
              </button>
              <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50" data-id="element-1729">
                Next
                <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1730" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default PolicySearch;